import { PDFDocument, PDFFont, PDFPage, rgb, StandardFonts } from 'pdf-lib';
import { convertAllDates, getOutdoorSizeDisplay, getOutdoorType, netHST, numToCurrency } from '../../../../utils/Functions';

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 36;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const BLACK = rgb(0, 0, 0);
const GRAY = rgb(0.4, 0.4, 0.4);
const LIGHT = rgb(0.92, 0.92, 0.92);

// The signature area is pinned to the bottom of the page at a fixed size (the BoldSign boxes don't scale);
// everything above it shrinks to fit the space that's left.
const SIGNATURE_AREA_HEIGHT = 128;
const CONTENT_BOTTOM = MARGIN + SIGNATURE_AREA_HEIGHT + 8;
const MIN_SCALE = 0.5;

// Offsets (in points) from a signature/date line up to the stored sign field y. savePdf's formatSignFields
// turns y into a 25px (~19pt) BoldSign box starting 15px (signature) / 10px (date) above it, so these
// put the bottom of each box just above its line.
const SIGN_TAB_OFFSET = 10;
const DATE_TAB_OFFSET = 13;
// Name fields get the same 25px box as signatures; this sits it on the Name line
const NAME_TAB_OFFSET = 7;

// Sign field keys/titles/indexes match the Sign Fields in utils/Constants (SalesRepSign, ExSign, etc.)
const SALES_REP_INDEX = -99;
const EXECUTOR_INDEX = -1;

export interface IApsSignField {
  index: number;
  key: string;
  pageNumber: number;
  x: number;
  y: number;
  title: string;
}

// [label, value, relative column width]
type Field = [string, string, number?];

// Standard Helvetica only encodes WinAnsi, so strip anything it can't draw instead of throwing
const clean = (value: any) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value)
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/[^\x20-\x7E\xA0-\xFF]/g, '?');
};

const formatDate = (value: any) => {
  if (!value) return '-';
  const date = new Date(value);
  if (isNaN(date.getTime())) return clean(value);
  // DOB/expiry are saved two ways: date pickers keep the local time of day they were entered at (so an evening
  // entry is already the next day in UTC), while date-only values are saved as exactly midnight UTC (which is
  // the evening before in Toronto). Only the latter should be read as a UTC day; everything else is local.
  const midnightUtc = date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0 && date.getUTCMilliseconds() === 0;
  const day = midnightUtc ? new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) : date;
  return convertAllDates(day, 'MMM d, yyyy');
};

const currency = (value: any) => (value || value === 0 ? numToCurrency.format(value) : '-');

// Writes top-down on a single page. Every size and gap is multiplied by `scale`; with `dry` set nothing is
// drawn, so a first pass can measure how tall the content is at full size.
class PdfWriter {
  y = PAGE_HEIGHT - MARGIN;

  constructor(public page: PDFPage, public font: PDFFont, public bold: PDFFont, public scale = 1, public dry = false) {}

  // Truncates text so it never runs into the next column
  fit(text: string, size: number, font: PDFFont, maxWidth: number) {
    let value = clean(text);
    if (font.widthOfTextAtSize(value, size) <= maxWidth) return value;
    while (value.length && font.widthOfTextAtSize(`${value}...`, size) > maxWidth) {
      value = value.slice(0, -1);
    }
    return `${value}...`;
  }

  text(text: string, x: number, y: number, size: number, font: PDFFont = this.font, color = BLACK, maxWidth = CONTENT_WIDTH) {
    if (this.dry) return;
    this.page.drawText(this.fit(text, size, font, maxWidth), { x, y, size, font, color });
  }

  line(x1: number, y1: number, x2: number, y2: number, thickness: number, color = BLACK) {
    if (this.dry) return;
    this.page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness, color });
  }

  header(projectName: string, suite: string) {
    const k = this.scale;
    this.text(projectName, MARGIN, this.y - 18 * k, 18 * k, this.bold);
    this.text(`Suite ${clean(suite)}`, MARGIN, this.y - 33 * k, 12 * k);
    this.text(`APS Review - Generated ${convertAllDates(new Date(), 'MMM d, yyyy')}`, MARGIN, this.y - 44 * k, 8 * k, this.font, GRAY);
    this.line(MARGIN, this.y - 50 * k, PAGE_WIDTH - MARGIN, this.y - 50 * k, 1);
    this.y -= 54 * k;
  }

  section(title: string) {
    const k = this.scale;
    this.y -= 4 * k;
    if (!this.dry) this.page.drawRectangle({ x: MARGIN, y: this.y - 14 * k, width: CONTENT_WIDTH, height: 14 * k, color: LIGHT });
    this.text(title, MARGIN + 4, this.y - 10.5 * k, 9 * k, this.bold);
    this.y -= 18 * k;
  }

  subheading(title: string) {
    const k = this.scale;
    this.text(title, MARGIN, this.y - 9 * k, 8.5 * k, this.bold);
    this.y -= 12 * k;
  }

  // One row of label/value pairs. Without `columns`, each field's width is its share of the row (default 1).
  // With `columns`, the row sits on a fixed grid so cells line up across rows; the third value is then how
  // many grid columns the field spans.
  row(fields: Field[], columns?: number) {
    const k = this.scale;
    const totalWeight = columns || fields.reduce((total, [, , weight]) => total + (weight || 1), 0);
    let x = MARGIN;
    fields.forEach(([label, value, weight]) => {
      const width = (CONTENT_WIDTH * (weight || 1)) / totalWeight;
      this.text(label, x, this.y - 7 * k, 6.5 * k, this.font, GRAY, width - 6);
      this.text(value, x, this.y - 17 * k, 8.5 * k, this.font, BLACK, width - 6);
      x += width;
    });
    this.y -= 20 * k;
  }

  note(text: string) {
    const k = this.scale;
    this.text(text, MARGIN, this.y - 9 * k, 8.5 * k, this.font, GRAY);
    this.y -= 12 * k;
  }

  // Simple table; columns listed in rightAlign are right-justified (for amounts). The last row is the bold total.
  table(headers: string[], rows: string[][], widths: number[], rightAlign: number[] = []) {
    const k = this.scale;
    const size = 8.5 * k;
    const drawRow = (cells: string[], font: PDFFont) => {
      let x = MARGIN;
      cells.forEach((cell, column) => {
        if (!this.dry) {
          const value = this.fit(cell, size, font, widths[column] - 8);
          const cellX = rightAlign.includes(column) ? x + widths[column] - font.widthOfTextAtSize(value, size) - 4 : x + 4;
          this.page.drawText(value, { x: cellX, y: this.y - 9 * k, size, font, color: BLACK });
        }
        x += widths[column];
      });
      this.y -= 12 * k;
    };

    drawRow(headers, this.bold);
    this.line(MARGIN, this.y + 1.5 * k, PAGE_WIDTH - MARGIN, this.y + 1.5 * k, 0.5, GRAY);
    rows.forEach((row, index) => {
      const isTotal = index === rows.length - 1;
      if (isTotal) this.line(MARGIN, this.y, PAGE_WIDTH - MARGIN, this.y, 0.5, GRAY);
      drawRow(row, isTotal ? this.bold : this.font);
    });
    this.y -= 3 * k;
  }

  gap(points: number) {
    this.y -= points * this.scale;
  }
}

const drawContent = (writer: PdfWriter, project: any, unit: any, deal: any) => {
  const purchasers: any[] = deal.purchasers || [];
  const realtors: any[] = deal.realtor || [];
  const options: any[] = deal.options || [];

  const basePrice = project.netHst ? netHST(unit.basePrice, project) : unit.basePrice;
  const totalOptions = options.reduce((total: number, option: any) => total + (option.amount || 0), 0);

  writer.header(project.name, unit.suite);

  // Unit
  writer.section('Unit Information');
  writer.row([
    ['Suite', clean(unit.suite)],
    ['Level', clean(unit.level)],
    ['Unit', clean(unit.unit)],
    ['Model Type', clean(unit.modelType)],
    ['Unit Type', clean(unit.unitType)],
    ['Exposure', clean(unit.exposure)],
  ]);
  writer.row([
    ['Bathroom', clean(unit.bathroom)],
    ['Outdoor Size', clean(unit.outdoor && unit.outdoor.length ? getOutdoorSizeDisplay(unit.outdoor) : '')],
    ['Outdoor Type', clean(getOutdoorType(unit.outdoor)), 2],
    [project.netHst ? 'Base Price (Net HST)' : 'Base Price', currency(basePrice), 2],
  ]);

  if (options.length) {
    writer.table(
      ['Option', 'Amount', 'Purchase Amount'],
      [
        ...options.map((option: any) => [clean(option.name), currency(option.amount), clean(option.purchaseAmount)]),
        ['Total Options', currency(totalOptions), ' '],
      ],
      [CONTENT_WIDTH - 220, 110, 110],
      [1, 2]
    );
  } else {
    writer.note('No options on this deal');
  }
  writer.row([[project.netHst ? 'Total Price (Net HST + Options)' : 'Total Price (Base Price + Options)', currency(basePrice + totalOptions)]]);

  // Purchasers
  writer.section(purchasers.length > 1 ? 'Purchasers' : 'Purchaser');
  if (!purchasers.length) writer.note('No purchasers on this deal');
  purchasers.forEach((purchaser: any, index: number) => {
    writer.subheading(`Purchaser ${index + 1} - ${clean(purchaser.firstName)} ${clean(purchaser.lastName)}`);
    // Fixed 4-column grid so every purchaser's fields line up; address and email span 2 columns
    writer.row(
      [
        ['First Name', clean(purchaser.firstName)],
        ['Last Name', clean(purchaser.lastName)],
        ['Date of Birth', formatDate(purchaser.dob)],
        ['SIN', clean(purchaser.sin)],
      ],
      4
    );
    writer.row(
      [
        ['Street Address', clean(purchaser.streetAddress), 2],
        ['City', clean(purchaser.city)],
        ['Province', clean(purchaser.province)],
      ],
      4
    );
    writer.row(
      [
        ['Postal Code', clean(purchaser.postalCode)],
        ['Country', clean(purchaser.country)],
        ['Primary Phone', clean(purchaser.primaryPhone)],
        ['Occupation', clean(purchaser.occupation)],
      ],
      4
    );
    writer.row(
      [
        ['Email', clean(purchaser.email), 2],
        ['Employer', clean(purchaser.employer)],
        ['ID Type', clean(purchaser.idType)],
      ],
      4
    );
    writer.row(
      [
        ['ID Number', clean(purchaser.idNumber)],
        ['ID Expiry', formatDate(purchaser.idExpiry)],
        ['ID Jurisdiction', clean(purchaser.idJurisdiction)],
      ],
      4
    );
    writer.gap(2);
  });

  // Realtors
  writer.section(realtors.length > 1 ? 'Realtors' : 'Realtor');
  if (!realtors.length) writer.note('No realtor on this deal');
  realtors.forEach((realtor: any) => {
    writer.row([
      ['First Name', clean(realtor.firstName)],
      ['Last Name', clean(realtor.lastName)],
      ['Brokerage', clean(realtor.brokerage), 2],
      ['Direct Phone', clean(realtor.directPhone)],
      ['Email', clean(realtor.email), 2],
    ]);
  });
};

// Draws one signer's block (name, signature line, date line) and returns its BoldSign sign fields.
// Pass name as null to leave a fillable name field for the signer instead (e.g. the Executor).
const signatureBlock = (writer: PdfWriter, x: number, top: number, width: number, title: string, name: string | null, index: number, prefix: string) => {
  const nameY = top - 26;
  const signLineY = top - 60;
  const dateLineY = top - 92;
  const dateWidth = Math.min(160, width);
  const nameFields: IApsSignField[] = [];

  writer.text(title, x, top - 11, 10, writer.bold, BLACK, width);
  if (name === null) {
    const labelWidth = writer.font.widthOfTextAtSize('Name: ', 9);
    writer.text('Name:', x, nameY, 9, writer.font, BLACK, width);
    writer.line(x + labelWidth, nameY - 2, x + width, nameY - 2, 0.5, GRAY);
    nameFields.push({
      index,
      key: 'name',
      title: `${prefix}Name`,
      pageNumber: 0,
      x: Math.round(x + labelWidth),
      y: Math.round(PAGE_HEIGHT - nameY - NAME_TAB_OFFSET),
    });
  } else {
    writer.text(`Name: ${name}`, x, nameY, 9, writer.font, BLACK, width);
  }

  writer.line(x, signLineY, x + width, signLineY, 0.75);
  writer.text('Signature', x, signLineY - 9, 7, writer.font, GRAY, width);
  writer.line(x, dateLineY, x + dateWidth, dateLineY, 0.75);
  writer.text('Date', x, dateLineY - 9, 7, writer.font, GRAY, width);

  // Sign field y is measured from the top of the page, same as the Merge Creator stores it
  return [
    ...nameFields,
    {
      index,
      key: 'signature',
      title: `${prefix}Sign`,
      pageNumber: 0,
      x: Math.round(x),
      y: Math.round(PAGE_HEIGHT - signLineY - SIGN_TAB_OFFSET),
    },
    {
      index,
      key: 'date',
      title: `${prefix}Date`,
      pageNumber: 0,
      x: Math.round(x),
      y: Math.round(PAGE_HEIGHT - dateLineY - DATE_TAB_OFFSET),
    },
  ];
};

export const generateApsReview = async (project: any, unit: any, deal: any) => {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const page = pdf.addPage([PAGE_WIDTH, PAGE_HEIGHT]);

  // Measure at full size, then shrink everything above the signatures just enough to fit
  const measure = new PdfWriter(page, font, bold, 1, true);
  drawContent(measure, project, unit, deal);
  const fullHeight = PAGE_HEIGHT - MARGIN - measure.y;
  const available = PAGE_HEIGHT - MARGIN - CONTENT_BOTTOM;
  const scale = Math.max(MIN_SCALE, Math.min(1, available / fullHeight));

  const writer = new PdfWriter(page, font, bold, scale);
  drawContent(writer, project, unit, deal);

  // Signatures, pinned to the bottom of the page at full size
  const signatures = new PdfWriter(page, font, bold);
  signatures.y = MARGIN + SIGNATURE_AREA_HEIGHT;
  signatures.section('Review and Approval');
  const top = signatures.y - 4;
  const columnWidth = (CONTENT_WIDTH - 30) / 2;
  const signFields: IApsSignField[] = [
    ...signatureBlock(signatures, MARGIN, top, columnWidth, 'Sales Representative', clean(deal.salesRep?.fullName), SALES_REP_INDEX, 'SalesRep'),
    ...signatureBlock(signatures, MARGIN + columnWidth + 30, top, columnWidth, 'Executor', null, EXECUTOR_INDEX, 'Ex'),
  ];

  const pdfBytes = await pdf.save();
  const file = new File([new Blob([pdfBytes])], `${project.name} - Suite ${unit.suite} - APS Review.pdf`, { type: 'application/pdf' });

  return { file, signFields };
};
